/**
 * Single source of truth for the product's brand name. Every user-
 * visible surface (page titles, PWA manifest, sticky header wordmark,
 * logo alt text) pulls from here so a rename is a one-line change
 * rather than a find-and-replace across the app.
 *
 * Descriptive copy in the product (settings blurbs, empty states,
 * help text) should NOT reference the brand by name — write in the
 * second person or describe the behaviour directly. Keeping those
 * free of the brand name means a future rename doesn't need a copy
 * pass, only a config flip here.
 */
export const BRAND_NAME = "OutRepR";

/** Tagline shown wherever a marketing line is needed.
 *
 *  v9.242 — relabelled from "One More Rep" → "Out Rep Yourself".
 *  The new tagline plays directly on the brand mark (OutRepR is
 *  visually + phonetically embedded in the imperative "Out Rep
 *  yourself"), making the connection between name and message
 *  explicit. Reads as a personal-best directive ("beat the
 *  version of you that showed up last time") rather than the
 *  generic "one more rep" gym chant.
 *
 *  Earlier revs:
 *    - "track your lifts like a portfolio" — RepLedger-era pun
 *    - "Train smarter. See every rep." — OutRepR-era placeholder
 *      that the user flagged as cringe
 *    - "One More Rep" (Sprint A → v9.241) — short, gym-floor-
 *      native, but disconnected from the brand name.
 *
 *  Currently rendered (changes here propagate everywhere):
 *    - Landing page (src/app/page.tsx) as the slogan line under
 *      the brand mark + name.
 *    - App shell header (src/app/(app)/layout.tsx) as a muted
 *      subtitle next to BRAND_NAME (visible on sm+ viewports;
 *      hidden on iPhone SE to avoid crowding the logo cluster).
 *    - Onboarding header + body callout (onboarding-wizard.tsx).
 *    - OG image (opengraph-image.tsx) as the line under the
 *      brand wordmark in the social-share card.
 *    - Default browser tab title format (src/app/layout.tsx):
 *      "OutRepR — Out Rep Yourself".
 *
 *  Callers may still render conditionally on
 *  `BRAND_TAGLINE.length > 0` to keep future rebrands clean. */
export const BRAND_TAGLINE = "Out Rep Yourself";
